import {
  HostAuthError,
  type HostAuthErrorReason,
  type HostErrorSubscriber,
  type HostRefreshReason,
  type HostToken,
  type HostTokenProfile,
  type HostTokenRequester,
} from "@healthos/auth/react";
import type { HealthOSBuildInfo } from "./build-info";

/**
 * Origins an embedding host can present. Covers the desktop sidecar Tauri
 * webview (origin differs by platform/version) and the Microsoft Teams wrapper
 * host page (one per environment). The allowlist must cover every shipped
 * origin or host-auth mode silently won't activate.
 */
const DESKTOP_PARENT_ORIGINS = [
  "tauri://localhost",
  "https://tauri.localhost",
  "http://tauri.localhost",
] as const;

/**
 * The Teams wrapper's local dev server origin. Only trusted in dev builds
 * (`import.meta.env.DEV`) so it never ships in the production allowlist.
 */
const LOCAL_TEAMS_DEV_ORIGIN = "https://localhost:4280";

const TEAMS_PARENT_ORIGINS = [
  "https://msteams-healthos.thriveglobal.com",
  "https://msteams-healthos.stag.thriveglobal.com",
  ...(import.meta.env.DEV ? [LOCAL_TEAMS_DEV_ORIGIN] : []),
] as const;

const TRUSTED_PARENT_ORIGINS = [...DESKTOP_PARENT_ORIGINS, ...TEAMS_PARENT_ORIGINS] as const;

const PROTOCOL_VERSION = 1;
const CHILD_REQUEST = "healthos:auth:request";
const CHILD_REFRESH = "healthos:auth:refresh";
const CHILD_LOGOUT = "healthos:auth:logout";
const CHILD_OPEN_EXTERNAL = "healthos:open-external";
const CHILD_VERSION = "healthos:version";
const PARENT_TOKEN = "healthos:auth:token";
const PARENT_ERROR = "healthos:auth:error";
const PARENT_PENDING = "healthos:auth:pending";
const PARENT_RELOAD = "healthos:reload";
const PARENT_VERSION_REQUEST = "healthos:version:request";
const CHILD_AUTH_POPUP_REQUEST = "healthos:auth-popup:request";
const PARENT_AUTH_POPUP_RESULT = "healthos:auth-popup:result";
const PARENT_AUTH_POPUP_ERROR = "healthos:auth-popup:error";
// Requests the host run the native Teams people picker and report back the
// selected members' Entra object ids. HealthOS runs nested inside the Teams
// wrapper's iframe, and Teams only relays SDK messages to the frame it
// directly created (the wrapper) - a grandchild iframe's own
// app.initialize() has no host to complete a handshake with and just times
// out ("SDK initialization timed out", confirmed never reaching
// isSupported() or selectPeople()). So the host runs the real teams-js call
// on our behalf, the same relay shape as CHILD_AUTH_POPUP_REQUEST above.
const CHILD_PEOPLE_PICKER_REQUEST = "healthos:people-picker:request";
const PARENT_PEOPLE_PICKER_RESULT = "healthos:people-picker:result";
const PARENT_PEOPLE_PICKER_ERROR = "healthos:people-picker:error";

// Per-heartbeat timeout: how long to wait for a token/error reply OR a
// pending heartbeat before giving up. The host (e.g. the Teams wrapper) posts
// a pending heartbeat every ~4s while it's still working - see
// PENDING_INTERVAL_MS in thrive-healthos-ms-teams-app's authBridge.ts - so
// this only needs to comfortably exceed that interval, not the worst-case
// duration of the whole flow. A host that never responds at all (no reply,
// no heartbeat) still fails within this window.
const HANDSHAKE_TIMEOUT_MS = 15_000;

// Absolute backstop regardless of how many heartbeats arrive. Set just past
// the wrapper's own 90s acquisition bound (see ACQUIRE_TIMEOUT_MS in
// thrive-healthos-ms-teams-app's tokenSource.ts), so this should not fire in
// normal operation - it exists only to guarantee we never wait forever if the
// heartbeat-clearing logic on either side has a bug.
const ACQUIRE_ABSOLUTE_TIMEOUT_MS = 100_000;

export type HostAuthContext = { channel: string; parentOrigin: string };

function isAllowlisted(origin: string | null): origin is (typeof TRUSTED_PARENT_ORIGINS)[number] {
  return origin !== null && (TRUSTED_PARENT_ORIGINS as readonly string[]).includes(origin);
}

/**
 * Pure G1 gate. Host-auth mode activates only when a supported `embed` marker
 * is present (`desktop` for the Tauri sidecar, `teams` for the Microsoft Teams
 * wrapper), a per-load `authChannel` nonce is present, and the parent frame's
 * origin is allowlisted. Any condition missing → null → the standalone OIDC
 * path runs unchanged. Split from window access so it is exhaustively
 * unit-testable.
 */
export function evaluateHostAuth(input: {
  search: string;
  parentOrigin: string | null;
}): HostAuthContext | null {
  const params = new URLSearchParams(input.search);
  const embed = params.get("embed");
  if (embed !== "desktop" && embed !== "teams") return null;
  const channel = params.get("authChannel");
  if (!channel) return null;
  if (!isAllowlisted(input.parentOrigin)) return null;
  return { channel, parentOrigin: input.parentOrigin };
}

function readParentOrigin(): string | null {
  if (typeof window === "undefined") return null;
  if (window.parent === window) return null;
  try {
    const ancestors = window.location.ancestorOrigins;
    if (ancestors && ancestors.length > 0) return ancestors[0] ?? null;
  } catch {
    /* ancestorOrigins unsupported; fall through to referrer */
  }
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      /* malformed referrer */
    }
  }
  return null;
}

export function detectHostAuth(): HostAuthContext | null {
  if (typeof window === "undefined") return null;
  return evaluateHostAuth({
    search: window.location.search,
    parentOrigin: readParentOrigin(),
  });
}

/**
 * The host-auth context captured at boot. `detectHostAuth` reads `embed=desktop`
 * and `authChannel` from the URL, but those query params only exist on the
 * initial iframe load; once the SPA navigates (e.g. to /connectors) they are
 * gone. Boot stashes the context here so later code (connector OAuth) can still
 * reach the host without re-parsing a URL that no longer carries the markers.
 */
let activeHostAuth: HostAuthContext | null = null;

const HOST_AUTH_STORAGE_KEY = "healthos:host-auth";

/**
 * Mirror the boot context into sessionStorage so a same-origin full-page
 * navigation that drops the query string can still recover it. Only the channel
 * nonce and the (already allowlisted) parent origin are stored, never a token.
 * sessionStorage is per-tab and cleared when the tab closes.
 */
function persistHostAuth(ctx: HostAuthContext): void {
  try {
    sessionStorage.setItem(HOST_AUTH_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* storage unavailable (private mode / disabled); in-memory still works */
  }
}

function readPersistedHostAuth(): HostAuthContext | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(HOST_AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<HostAuthContext>;
    if (typeof parsed?.channel !== "string" || parsed.channel.length === 0) return null;
    // Re-validate the origin against the allowlist; a tampered value cannot
    // widen the set of parents we will postMessage to.
    const parentOrigin = parsed.parentOrigin ?? null;
    if (!isAllowlisted(parentOrigin)) return null;
    return { channel: parsed.channel, parentOrigin };
  } catch {
    return null;
  }
}

export function setActiveHostAuth(ctx: HostAuthContext | null): void {
  activeHostAuth = ctx;
}

/**
 * Resolve the host-auth context at boot and make it durable for the SPA's
 * lifetime. The URL is the source of truth whenever the embed markers are
 * present (every shell-built iframe load carries a fresh authChannel), and that
 * value is mirrored into sessionStorage. If a same-origin full-page navigation
 * lands without the markers, the stored context is used instead; the shell has
 * not remounted the iframe, so its channel nonce still matches.
 */
export function initHostAuth(): HostAuthContext | null {
  const fromUrl = detectHostAuth();
  if (fromUrl) {
    persistHostAuth(fromUrl);
    setActiveHostAuth(fromUrl);
    return fromUrl;
  }
  const fromStorage = readPersistedHostAuth();
  setActiveHostAuth(fromStorage);
  return fromStorage;
}

export function getActiveHostAuth(): HostAuthContext | null {
  if (activeHostAuth) return activeHostAuth;
  // Lazy recovery: a fresh document (full-page nav) may read this before boot
  // wiring runs. The stored channel is still valid while the shell holds the
  // same nonce.
  activeHostAuth = readPersistedHostAuth();
  return activeHostAuth;
}

/**
 * Distinguishes the Microsoft Teams wrapper from the desktop sidecar among
 * the allowlisted host-auth parent origins.
 */
export function isTeamsParentOrigin(origin: string | null): boolean {
  return origin !== null && (TEAMS_PARENT_ORIGINS as readonly string[]).includes(origin);
}

/**
 * True when healthOS is currently embedded inside the Microsoft Teams auth
 * wrapper (as opposed to the desktop sidecar or the standalone web app).
 */
export function isInMsTeamsAuthWrapper(): boolean {
  return isTeamsParentOrigin(getActiveHostAuth()?.parentOrigin ?? null);
}

export function isDesktopParentOrigin(origin: string | null): boolean {
  return origin !== null && (DESKTOP_PARENT_ORIGINS as readonly string[]).includes(origin);
}

/**
 * True when healthOS is currently embedded inside the desktop sidecar's Tauri
 * webview (as opposed to the Teams wrapper or the standalone web app).
 */
export function isInDesktopEmbed(): boolean {
  return isDesktopParentOrigin(getActiveHostAuth()?.parentOrigin ?? null);
}

function isProfile(value: unknown): value is HostTokenProfile {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.sub === "string";
}

/**
 * Parse a validated parent message into a token, an error, or null when the
 * message is not for us (wrong version/channel/type or malformed). Origin and
 * source are validated by the listener before this runs.
 */
export function parseParentMessage(
  data: unknown,
  channel: string,
): { token: HostToken } | { error: HostAuthError } | { pending: true } | null {
  if (typeof data !== "object" || data === null) return null;
  const msg = data as Record<string, unknown>;
  if (msg.v !== PROTOCOL_VERSION || msg.channel !== channel) return null;
  if (msg.type === PARENT_TOKEN) {
    if (typeof msg.accessToken !== "string") return null;
    return {
      token: {
        accessToken: msg.accessToken,
        expiresAt: typeof msg.expiresAt === "number" ? msg.expiresAt : null,
        profile: isProfile(msg.profile) ? msg.profile : undefined,
      },
    };
  }
  if (msg.type === PARENT_ERROR) {
    const reason = msg.code === "logged_out" ? "logged_out" : "refresh_failed";
    return { error: new HostAuthError(reason) };
  }
  if (msg.type === PARENT_PENDING) return { pending: true };
  return null;
}

/**
 * Build the postMessage transport the host-auth session calls. Each invocation
 * posts a request (or a refresh, with a reason) to the allowlisted parent at an
 * exact origin (never `"*"`) and resolves with the first matching token, or
 * rejects with a HostAuthError on a parent error or timeout. The token never
 * touches the URL, logs, or storage.
 */
export function createDesktopTokenRequester(ctx: HostAuthContext): HostTokenRequester {
  return (reason?: HostRefreshReason) =>
    new Promise<HostToken>((resolve, reject) => {
      let settled = false;
      let timer: number | undefined;

      function fail() {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new HostAuthError("timeout"));
      }

      // Re-armed on every pending heartbeat, so we only time out when the
      // host has gone quiet for HANDSHAKE_TIMEOUT_MS - not when the overall
      // flow has merely taken a while.
      function armTimer() {
        window.clearTimeout(timer);
        timer = window.setTimeout(fail, HANDSHAKE_TIMEOUT_MS);
      }

      const absoluteTimer = window.setTimeout(fail, ACQUIRE_ABSOLUTE_TIMEOUT_MS);

      const onMessage = (event: MessageEvent) => {
        if (settled) return;
        if (event.source !== window.parent) return;
        if (!isAllowlisted(event.origin)) return;
        const parsed = parseParentMessage(event.data, ctx.channel);
        if (!parsed) return;
        if ("pending" in parsed) {
          armTimer();
          return;
        }
        settled = true;
        cleanup();
        if ("token" in parsed) resolve(parsed.token);
        else reject(parsed.error);
      };
      function cleanup() {
        window.removeEventListener("message", onMessage);
        window.clearTimeout(timer);
        window.clearTimeout(absoluteTimer);
      }
      window.addEventListener("message", onMessage);
      armTimer();
      const payload = reason
        ? { v: PROTOCOL_VERSION, type: CHILD_REFRESH, channel: ctx.channel, reason }
        : { v: PROTOCOL_VERSION, type: CHILD_REQUEST, channel: ctx.channel };
      window.parent.postMessage(payload, ctx.parentOrigin);
    });
}

/**
 * Origins the popup itself is allowed to navigate to before the host will
 * treat it as a real auth flow. Guards `createAuthPopupRequester` against a
 * caller (or a compromised parent) asking the host to pop an arbitrary URL:
 * every provider this seam serves today (Google, Microsoft, Terra) redirects
 * through one of these before landing back on our own callback.
 */
const AUTH_POPUP_ALLOWED_ORIGINS = [
  "https://accounts.google.com",
  "https://login.microsoftonline.com",
  "https://api.tryterra.co",
  "https://widget.tryterra.co",
] as const;

/**
 * True when `url` is `https:` and its origin is one a provider consent screen
 * legitimately runs on. Exported so the popup seam's guard is independently
 * unit-testable without going through the full postMessage round-trip.
 */
export function isAllowedAuthPopupUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === "https:" &&
      (AUTH_POPUP_ALLOWED_ORIGINS as readonly string[]).includes(parsed.origin)
    );
  } catch {
    return false;
  }
}

/**
 * A reply message either resolves the request with a value or rejects it
 * with an error; `undefined` means the message is not a reply this request
 * recognizes (wrong type) and should be ignored, same as an unrecognized
 * message is ignored everywhere else in this file.
 */
type HostReply<T> = { value: T } | { error: Error } | undefined;

/**
 * Generic transport for a request/response pair over the host-auth bridge:
 * post a request to the allowlisted parent, then resolve or reject based on
 * typed reply messages on the same channel. Every capability that needs the
 * host to run something on its behalf and report back - an interactive auth
 * popup, the Teams people picker - shares this exact shape (origin/source/
 * version/channel validation, a timeout ceiling, exactly-once settlement);
 * only the request payload and how a reply resolves or rejects differ, which
 * `parseReply` captures per capability.
 */
function requestFromHost<T>(
  ctx: HostAuthContext,
  request: Record<string, unknown>,
  parseReply: (msg: Record<string, unknown>) => HostReply<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let settled = false;

    function cleanup() {
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
    }

    function fail(err: Error) {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    }

    function succeed(value: T) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(value);
    }

    // Every request through here can involve an interactive host-side flow
    // (a full consent screen, a live search picker) with no fixed duration,
    // so each gets the same absolute ceiling as the desktop token requester
    // rather than a shorter silent-request timeout.
    const timer = window.setTimeout(() => {
      console.error("[host-auth] timed out waiting for the Teams host", {
        type: request.type,
        parentOrigin: ctx.parentOrigin,
        channel: ctx.channel,
      });
      fail(new Error("Timed out waiting for the Teams host to respond"));
    }, ACQUIRE_ABSOLUTE_TIMEOUT_MS);

    const onMessage = (event: MessageEvent) => {
      if (settled) return;
      if (event.source !== window.parent) return;
      if (!isAllowlisted(event.origin)) return;
      if (typeof event.data !== "object" || event.data === null) return;
      const msg = event.data as Record<string, unknown>;
      if (msg.v !== PROTOCOL_VERSION || msg.channel !== ctx.channel) return;
      const reply = parseReply(msg);
      if (!reply) return;
      console.debug("[host-auth] received parent reply", { type: msg.type });
      if ("value" in reply) succeed(reply.value);
      else fail(reply.error);
    };
    window.addEventListener("message", onMessage);
    console.debug("[host-auth] posting request to parent", {
      type: request.type,
      parentOrigin: ctx.parentOrigin,
      channel: ctx.channel,
    });
    window.parent.postMessage(
      { v: PROTOCOL_VERSION, channel: ctx.channel, ...request },
      ctx.parentOrigin,
    );
  });
}

/**
 * Build the transport that asks the Teams host to open a URL as an auth
 * popup and resolve once the flow completes, for any provider whose consent
 * screen cannot render inside the Teams tab iframe (Entra's `frame-ancestors`
 * blocks it the same way Google's and Terra's do). Provider-agnostic by
 * design: the child only ever hands the host a URL to pop and waits for a
 * result, the same shape as `createDesktopExternalOpener` above but with a
 * reply instead of fire-and-forget. Posts to the allowlisted parent at an
 * exact origin (never `"*"`).
 */
export function createAuthPopupRequester(ctx: HostAuthContext): (url: string) => Promise<void> {
  return (url: string) => {
    if (!isAllowedAuthPopupUrl(url)) {
      console.error("[host-auth] refusing auth popup for untrusted URL", { url });
      return Promise.reject(new Error("Refusing to open an auth popup for an untrusted URL"));
    }
    return requestFromHost<void>(ctx, { type: CHILD_AUTH_POPUP_REQUEST, url }, (msg) => {
      if (msg.type === PARENT_AUTH_POPUP_RESULT) return { value: undefined };
      if (msg.type === PARENT_AUTH_POPUP_ERROR) {
        const message = typeof msg.message === "string" ? msg.message : "Auth popup request failed";
        return { error: new Error(message) };
      }
      return undefined;
    });
  };
}

/**
 * A people-picker request failed or was cancelled. `reason` lets a caller
 * tell a member-initiated cancel apart from a genuine failure without
 * parsing message text - the wire-level analog of teams-js's own
 * ErrorCode.USER_ABORT distinction, translated across the host-auth bridge.
 */
export class HostPeoplePickerError extends Error {
  readonly reason: "cancelled" | "unsupported" | "failed";

  constructor(reason: "cancelled" | "unsupported" | "failed", message: string) {
    super(message);
    this.name = "HostPeoplePickerError";
    this.reason = reason;
  }
}

/**
 * Build the transport that asks the Teams host to run the native people
 * picker and resolve with the selected members' Entra object ids. HealthOS
 * runs nested inside the Teams wrapper's iframe, and Teams only relays SDK
 * messages to the frame it directly created (the wrapper) - a grandchild
 * iframe's own `app.initialize()` has no host to complete a handshake with
 * and just times out. So this asks the host to run the real teams-js call on
 * our behalf, the same relay shape as `createAuthPopupRequester` above.
 */
export function createPeoplePickerRequester(
  ctx: HostAuthContext,
): (title: string) => Promise<string[]> {
  return (title: string) =>
    requestFromHost<string[]>(ctx, { type: CHILD_PEOPLE_PICKER_REQUEST, title }, (msg) => {
      if (msg.type === PARENT_PEOPLE_PICKER_RESULT) {
        const objectIds = Array.isArray(msg.objectIds)
          ? msg.objectIds.filter((id): id is string => typeof id === "string")
          : [];
        return { value: objectIds };
      }
      if (msg.type === PARENT_PEOPLE_PICKER_ERROR) {
        const reason =
          msg.reason === "cancelled" || msg.reason === "unsupported" ? msg.reason : "failed";
        const message =
          typeof msg.message === "string" ? msg.message : "People picker request failed";
        return { error: new HostPeoplePickerError(reason, message) };
      }
      return undefined;
    });
}

/**
 * Build the transport that asks the desktop host to end the session. Posts a
 * single logout message to the allowlisted parent at an exact origin; the
 * desktop performs the real SSO logout and broadcasts the result back, which
 * the error subscriber turns into a coherent teardown. Fire-and-forget; the
 * teardown arrives via the subscriber, not a reply here.
 */
export function createDesktopLogoutRequester(ctx: HostAuthContext): () => void {
  return () => {
    window.parent.postMessage(
      { v: PROTOCOL_VERSION, type: CHILD_LOGOUT, channel: ctx.channel },
      ctx.parentOrigin,
    );
  };
}

/**
 * Build a transport that asks the embedding host to open a URL in the system
 * browser instead of navigating the embedded iframe. Used by connector OAuth so
 * providers that reject embedded webviews complete in a real browser and
 * deep-link back into the app. Posts to the allowlisted parent at an exact
 * origin (never `"*"`). Fire-and-forget; the result returns via a deep link, not
 * a reply here. Only `https:` URLs are forwarded.
 *
 * Despite the name, this isn't desktop-only: the Teams wrapper handles the same
 * `CHILD_OPEN_EXTERNAL` message too (via the Teams JS SDK's `app.openLink`), so
 * this same function also backs the Teams "open in system browser" fallback for
 * Terra providers `createAuthPopupRequester`'s allowlist doesn't trust (see
 * `useTeamsPopup` in connector-auth-handoff.ts).
 */
export function createDesktopExternalOpener(ctx: HostAuthContext): (url: string) => void {
  return (url: string) => {
    let safe: URL;
    try {
      safe = new URL(url);
    } catch {
      return;
    }
    if (safe.protocol !== "https:") return;
    window.parent.postMessage(
      { v: PROTOCOL_VERSION, type: CHILD_OPEN_EXTERNAL, channel: ctx.channel, url: safe.href },
      ctx.parentOrigin,
    );
  };
}

/**
 * Build the transport for unsolicited parent logouts. The desktop broadcasts a
 * logout/expiry the user triggered elsewhere as a `logged_out` error with no
 * request outstanding; this persistent listener (same origin/source/channel
 * checks as the requester) forwards it so the session tears down.
 *
 * Only `logged_out` propagates here. A `refresh_failed` error is always a reply
 * to an in-flight token request: the requester promise already surfaces it, and
 * the token/fetch seam decides whether it's terminal (recoverAuthAfter401 →
 * reportError) or a transient hiccup to degrade through. Forwarding it here too
 * would let a single transient refresh failure end an otherwise healthy session.
 */
export function createDesktopErrorSubscriber(ctx: HostAuthContext): HostErrorSubscriber {
  return (onError: (reason: HostAuthErrorReason) => void) => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) return;
      if (!isAllowlisted(event.origin)) return;
      const parsed = parseParentMessage(event.data, ctx.channel);
      if (parsed && "error" in parsed && parsed.error.reason === "logged_out") {
        onError(parsed.error.reason);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  };
}

/**
 * Build the listener for an unsolicited parent reload command. The desktop
 * shell posts this to recover the embedded page in place (e.g. after an OTA
 * update); same origin/source/version/channel checks as the other listeners,
 * then a hard `window.location.reload()`. Fire-and-forget - no reply is sent.
 */
export function createDesktopReloadSubscriber(ctx: HostAuthContext): () => void {
  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    if (!isAllowlisted(event.origin)) return;
    if (typeof event.data !== "object" || event.data === null) return;
    const msg = event.data as Record<string, unknown>;
    if (msg.v !== PROTOCOL_VERSION || msg.channel !== ctx.channel || msg.type !== PARENT_RELOAD)
      return;
    window.location.reload();
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

/**
 * Build the responder that tells the desktop shell which HealthOS build is
 * running. The shell renders us in a cross-origin iframe, so it cannot read our
 * `window.__HEALTHOS__` directly - it asks over the bridge instead. We announce
 * the build once on wire-up (covering the case where the shell's request lands
 * before this listener is attached) and reply to every `healthos:version:request`
 * carrying our channel. Same origin/source/version/channel checks as the other
 * listeners. The payload is public build provenance (version, commit, release
 * time), never a token.
 */
export function createDesktopVersionResponder(
  ctx: HostAuthContext,
  info: HealthOSBuildInfo,
): () => void {
  const announce = () => {
    window.parent.postMessage(
      {
        v: PROTOCOL_VERSION,
        type: CHILD_VERSION,
        channel: ctx.channel,
        version: info.version,
        commit: info.commit,
        releaseTime: info.releaseTime,
      },
      ctx.parentOrigin,
    );
  };

  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    if (!isAllowlisted(event.origin)) return;
    if (typeof event.data !== "object" || event.data === null) return;
    const msg = event.data as Record<string, unknown>;
    if (msg.v !== PROTOCOL_VERSION || msg.channel !== ctx.channel) return;
    if (msg.type !== PARENT_VERSION_REQUEST) return;
    announce();
  };

  window.addEventListener("message", onMessage);
  announce();
  return () => window.removeEventListener("message", onMessage);
}
